import { useEffect, useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
import { Search } from 'lucide-react';
import { Sheet, SheetContent, SheetHeader, SheetTitle } from './ui/sheet';
import { Input } from './ui/input';
import { useAppShell } from './AppShellContext';
import { useDomains, useGuides } from '@/lib/queries';
import { cn } from '@/lib/utils';

/**
 * Global search — fuzzy substring match against domains + guides.
 * Phase 1.5 ships only those two corpora; later phases extend to
 * logs, projects, equipment, vendors as they arrive.
 *
 * Shown as a bottom sheet on mobile, centered modal on desktop. The
 * bottom-nav Search cell opens this; later phases will also wire a
 * `/` keyboard shortcut on desktop.
 */
export default function SearchSheet() {
  const { searchOpen, closeSearch } = useAppShell();
  const navigate = useNavigate();
  const [q, setQ] = useState('');

  // Reset the query each time the sheet opens so a stale search
  // doesn't linger.
  useEffect(() => {
    if (searchOpen) setQ('');
  }, [searchOpen]);

  const domains = useDomains();
  const guides = useGuides();

  const results = useMemo(() => {
    const needle = q.trim().toLowerCase();
    if (!needle) return [] as Array<{ kind: 'domain' | 'guide'; href: string; title: string; sub?: string }>;
    const out: Array<{ kind: 'domain' | 'guide'; href: string; title: string; sub?: string }> = [];
    const domainList = Array.isArray(domains.data) ? domains.data : [];
    const guideList = Array.isArray(guides.data) ? guides.data : [];
    for (const d of domainList) {
      if (d.name.toLowerCase().includes(needle) || d.slug.includes(needle)) {
        out.push({ kind: 'domain', href: `/domains/${d.slug}`, title: d.name, sub: d.description ?? undefined });
      }
    }
    for (const g of guideList) {
      // Hay only on title + body (truncated). Summary/tags are no
      // longer part of the wire shape since the redesign moved
      // guides to user-authored markdown.
      const hay = `${g.title} ${g.bodyMd.slice(0, 500)}`.toLowerCase();
      if (hay.includes(needle)) {
        out.push({ kind: 'guide', href: `/guides/${g.slug}`, title: g.title });
      }
    }
    return out.slice(0, 30);
  }, [q, domains.data, guides.data]);

  return (
    <Sheet open={searchOpen} onOpenChange={(o) => (o ? null : closeSearch())}>
      <SheetContent side="bottom" className="max-h-[80vh] overflow-y-auto">
        <SheetHeader>
          <SheetTitle>Search</SheetTitle>
        </SheetHeader>
        <div className="pt-3 pb-1">
          <div className="relative">
            <Search className="pointer-events-none absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" aria-hidden />
            <Input
              autoFocus
              type="search"
              placeholder="Search domains and guides…"
              value={q}
              onChange={(e) => setQ(e.target.value)}
              className="pl-9"
              data-testid="search-input"
            />
          </div>
        </div>

        <ul className="divide-y divide-rule pb-2" data-testid="search-results">
          {results.length === 0 && q.trim() ? (
            <li className="py-6 text-center text-sm text-muted-foreground">No matches.</li>
          ) : null}
          {results.map((r) => (
            <li key={`${r.kind}:${r.href}`}>
              <button
                type="button"
                onClick={() => {
                  navigate(r.href);
                  closeSearch();
                }}
                className={cn(
                  'flex w-full items-start gap-3 px-1 py-3 text-left',
                  'hover:bg-muted/50 transition-colors',
                )}
              >
                <span className="font-mono text-[10px] uppercase tracking-wider text-muted-foreground pt-1.5 w-14 shrink-0">
                  {r.kind}
                </span>
                <span className="min-w-0 flex-1">
                  <span className="block font-medium">{r.title}</span>
                  {r.sub ? <span className="block text-sm text-muted-foreground line-clamp-1">{r.sub}</span> : null}
                </span>
              </button>
            </li>
          ))}
        </ul>
      </SheetContent>
    </Sheet>
  );
}
